import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  acceptedNamesFor,
  flagUrlFor,
  type CountryMetadata,
} from "../data/countries";
import { loadState, saveState } from "../state/storage";
import {
  isRegionCleared,
  markRegionCleared,
  recordCorrect,
  recordWrong,
} from "../state/stats";
import { fuzzyMatch, findClosestCountry } from "../lib/fuzzy";
import {
  startRound,
  drawNext,
  returnMissed,
  type RoundState,
} from "../lib/selection";
import { pickDistractors } from "../lib/distractors";
import { getCountriesForRegion } from "./region-picker";

type Phase =
  | "prompting-text"
  | "prompting-mc"
  | "reveal-correct"
  | "reveal-wrong";

interface SessionState {
  phase: Phase;
  current: CountryMetadata | null;
  correctCount: number;
  wrongCount: number;
  lastFuzzyNote: string | null;
  guessedCountry: CountryMetadata | null;
  mcChoices: CountryMetadata[];
  usedHint: boolean;
  missedIsos: Set<string>;
}

// Auto-advance after a correct answer. Long enough to glance at the
// flag + fact panel; the user can also hit Enter / Next to skip ahead.
const AUTO_ADVANCE_MS = 2800;
const MC_TRIGGER_CONSECUTIVE_WRONG = 2;
const MC_CHOICE_COUNT = 4;

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildHtml = (): string => `
  <button class="learn-back" type="button" aria-label="Back to regions">← Regions</button>
  <div class="learn-progress">
    <span class="learn-correct">0</span> ✓
    <span class="learn-divider">·</span>
    <span class="learn-wrong">0</span> ✗
  </div>
  <section class="learn-panel" aria-live="polite">
    <p class="learn-prompt">What country is this?</p>
    <p class="learn-hint-text" hidden></p>
    <form class="learn-form">
      <input
        type="text"
        class="learn-input"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        placeholder="Type the country name"
        aria-label="Country name"
      />
      <button type="button" class="learn-hint-btn" hidden>Hint</button>
      <button type="submit" class="learn-submit">Submit</button>
    </form>
    <div class="learn-choices" role="group" aria-label="Choose the country" hidden></div>
    <div class="learn-feedback" hidden></div>
    <div class="learn-fact" hidden>
      <img class="learn-fact-flag" alt="" />
      <div class="learn-fact-body">
        <div class="learn-fact-capital"></div>
        <p class="learn-fact-text"></p>
        <p class="learn-fact-flag-desc"></p>
      </div>
    </div>
  </section>
`;

export const mountLearnSession = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  const region = root.dataset.region;
  if (!region) {
    console.error("[learn] missing region in dataset");
    ctx.nav.goRegionPicker();
    return () => {};
  }

  root.classList.add("view-learn");
  root.innerHTML = buildHtml();

  const backBtn = root.querySelector(".learn-back") as HTMLButtonElement;
  const form = root.querySelector(".learn-form") as HTMLFormElement;
  const input = root.querySelector(".learn-input") as HTMLInputElement;
  const submitBtn = root.querySelector(".learn-submit") as HTMLButtonElement;
  const hintBtn = root.querySelector(".learn-hint-btn") as HTMLButtonElement;
  const hintText = root.querySelector(".learn-hint-text") as HTMLParagraphElement;
  const feedback = root.querySelector(".learn-feedback") as HTMLDivElement;
  const factEl = root.querySelector(".learn-fact") as HTMLDivElement;
  const factFlag = root.querySelector(".learn-fact-flag") as HTMLImageElement;
  const factCapital = root.querySelector(".learn-fact-capital") as HTMLDivElement;
  const factText = root.querySelector(".learn-fact-text") as HTMLParagraphElement;
  const factFlagDesc = root.querySelector(".learn-fact-flag-desc") as HTMLParagraphElement;
  const choicesEl = root.querySelector(".learn-choices") as HTMLDivElement;
  const correctEl = root.querySelector(".learn-correct") as HTMLSpanElement;
  const wrongEl = root.querySelector(".learn-wrong") as HTMLSpanElement;
  const promptEl = root.querySelector(".learn-prompt") as HTMLParagraphElement;

  ctx.globe.setHoverEnabled(false);

  const table = await loadCountries();
  const grouped = groupByRegion(table);
  let appState = loadState();
  const tolerance = appState.settings.fuzzyMatchTolerance;
  const hintsEnabled = appState.settings.showHints;

  const regionCountries = getCountriesForRegion(region, grouped, appState);
  const regionIsos = regionCountries.map((c) => c.iso);

  const allCountriesForIntent = Object.values(table).map((c) => ({
    iso: c.iso,
    candidates: acceptedNamesFor(c),
  }));
  const INTENT_TOLERANCE = 3;

  if (regionCountries.length === 0) {
    promptEl.textContent = `No countries in "${region}" — pick another region.`;
    form.hidden = true;
    return () => {
      ctx.globe.setHoverEnabled(true);
      root.classList.remove("view-learn");
    };
  }

  const session: SessionState = {
    phase: "prompting-text",
    current: null,
    correctCount: 0,
    wrongCount: 0,
    lastFuzzyNote: null,
    guessedCountry: null,
    mcChoices: [],
    usedHint: false,
    missedIsos: new Set<string>(),
  };

  let round: RoundState = startRound(regionIsos, Math.random);

  let autoAdvanceTimer: number | null = null;
  let destroyed = false;

  const cancelAutoAdvance = (): void => {
    if (autoAdvanceTimer !== null) {
      window.clearTimeout(autoAdvanceTimer);
      autoAdvanceTimer = null;
    }
  };

  const renderMCChoices = (): void => {
    choicesEl.replaceChildren();
    for (const choice of session.mcChoices) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "learn-choice";
      btn.dataset.iso = choice.iso;
      btn.textContent = choice.canonicalName;
      btn.addEventListener("click", () => onMCClick(choice));
      choicesEl.appendChild(btn);
    }
  };

  const setMode = (phase: Phase): void => {
    session.phase = phase;
    if (phase === "prompting-mc") {
      form.hidden = true;
      choicesEl.hidden = false;
    } else {
      form.hidden = false;
      choicesEl.hidden = true;
    }
  };

  const advance = async (): Promise<void> => {
    if (destroyed) return;
    cancelAutoAdvance();
    const draw = drawNext(round, Math.random);
    round = draw.state;
    const pickedIso = draw.iso;
    if (!pickedIso) {
      promptEl.textContent = "Out of countries.";
      form.hidden = true;
      choicesEl.hidden = true;
      return;
    }
    const picked = table[pickedIso];
    if (!picked) {
      console.warn(`[learn] missing metadata for ${pickedIso}`);
      return;
    }
    session.current = picked;
    session.lastFuzzyNote = null;
    session.guessedCountry = null;
    session.usedHint = false;
    ctx.globe.setGuessIso(null);
    promptEl.textContent = "What country is this?";
    feedback.hidden = true;
    feedback.textContent = "";
    feedback.className = "learn-feedback";
    factEl.hidden = true;
    hintText.hidden = true;
    hintText.textContent = "";
    submitBtn.disabled = false;
    submitBtn.textContent = "Submit";
    hintBtn.disabled = false;
    hintBtn.hidden = !hintsEnabled;
    input.disabled = false;
    input.value = "";

    const stats = appState.stats[picked.iso];
    const useMC =
      (stats?.consecutiveWrong ?? 0) >= MC_TRIGGER_CONSECUTIVE_WRONG;

    if (useMC) {
      const distractors = pickDistractors({
        target: picked,
        pool: regionCountries,
        count: MC_CHOICE_COUNT - 1,
        rng: Math.random,
      });
      const all = [picked, ...distractors];
      // Fisher-Yates shuffle of the 4 options
      for (let i = all.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const t = all[i]!;
        all[i] = all[j]!;
        all[j] = t;
      }
      session.mcChoices = all;
      renderMCChoices();
      promptEl.textContent = "Pick the country:";
      setMode("prompting-mc");
    } else {
      session.mcChoices = [];
      setMode("prompting-text");
      input.focus();
    }

    ctx.globe.setTargetIso(picked.iso);
    void ctx.globe.flyToIso(picked.iso, { durationMs: 800 });
  };

  const showReveal = (correct: boolean): void => {
    if (!session.current) return;
    const country = session.current;
    feedback.hidden = false;
    feedback.className = `learn-feedback ${correct ? "is-correct" : "is-wrong"}`;
    const lines: string[] = [];
    if (correct) {
      lines.push(`<strong>✓ ${escapeHtml(country.canonicalName)}</strong>`);
      if (session.lastFuzzyNote) {
        lines.push(`<small>${escapeHtml(session.lastFuzzyNote)}</small>`);
      } else if (session.usedHint) {
        lines.push(
          `<small>Hint used — credit kept neutral (no promotion or demotion).</small>`,
        );
      }
    } else {
      lines.push(`<strong>✗ ${escapeHtml(country.canonicalName)}</strong>`);
      if (session.guessedCountry) {
        lines.push(
          `<small>You guessed <strong>${escapeHtml(session.guessedCountry.canonicalName)}</strong> — shown in red.</small>`,
        );
      } else {
        lines.push(`<small>Tap Next when you're ready to continue.</small>`);
      }
    }
    feedback.innerHTML = lines.join("<br/>");

    // Flag + fact panel. Shown on both correct and wrong reveals — the
    // wrong path is the higher-retention moment, so we want the info there.
    const flagUrl = flagUrlFor(country);
    if (flagUrl) {
      factFlag.src = flagUrl;
      factFlag.alt = `Flag of ${country.canonicalName}`;
      factFlag.hidden = false;
    } else {
      factFlag.removeAttribute("src");
      factFlag.hidden = true;
    }
    if (country.fact) {
      factCapital.innerHTML = country.fact.capital
        ? `Capital: <strong>${escapeHtml(country.fact.capital)}</strong>`
        : "";
      factCapital.hidden = !country.fact.capital;
      factText.textContent = country.fact.fact;
      factFlagDesc.textContent = country.fact.flagDescription;
      factEl.hidden = false;
    } else {
      factEl.hidden = true;
    }

    // After reveal, neither input nor MC choices are interactive
    form.hidden = false;
    choicesEl.hidden = true;
    input.disabled = true;
    hintBtn.disabled = true;
    submitBtn.disabled = false;
    submitBtn.textContent = "Next";
    requestAnimationFrame(() => submitBtn.focus());
  };

  // Common post-answer handler. `correct` is whether the answer was right;
  // `rawInput` is the user's text for findClosestCountry on the wrong path.
  const finalizeAnswer = (correct: boolean, rawInput: string): void => {
    if (!session.current) return;
    const now = Date.now();
    const iso = session.current.iso;

    if (correct) {
      session.phase = "reveal-correct";
      session.correctCount += 1;
      correctEl.textContent = String(session.correctCount);
      // Hint-used correct answers register as "seen" but neither promote nor
      // demote the country (half-credit handling per the spec).
      if (session.usedHint) {
        // Update lastSeen but don't change level / streaks.
        const prevStats = appState.stats[iso];
        if (prevStats) {
          appState = {
            ...appState,
            stats: {
              ...appState.stats,
              [iso]: { ...prevStats, lastSeen: now },
            },
          };
        }
      } else {
        appState = recordCorrect(appState, iso, now);
      }
      const cleared = isRegionCleared(appState, regionIsos);
      const alreadyMarked = appState.regionProgress[region]?.cleared ?? false;
      const justClearedRegion =
        cleared &&
        !alreadyMarked &&
        region !== "Mixed (cleared regions)";
      if (justClearedRegion) {
        appState = markRegionCleared(appState, region, now);
      }
      saveState(appState);
      showReveal(true);
      if (justClearedRegion) {
        autoAdvanceTimer = window.setTimeout(() => {
          ctx.nav.goRegionCleared({ region });
        }, AUTO_ADVANCE_MS);
      } else {
        autoAdvanceTimer = window.setTimeout(() => {
          void advance();
        }, AUTO_ADVANCE_MS);
      }
    } else {
      session.phase = "reveal-wrong";
      session.wrongCount += 1;
      session.missedIsos.add(iso);
      wrongEl.textContent = String(session.wrongCount);
      appState = recordWrong(appState, iso, now);
      saveState(appState);
      round = returnMissed(round, iso, Math.random);

      if (rawInput.length > 0) {
        const guess = findClosestCountry(
          rawInput,
          allCountriesForIntent,
          INTENT_TOLERANCE,
        );
        if (guess && guess.iso !== iso) {
          const guessed = table[guess.iso];
          if (guessed) {
            session.guessedCountry = guessed;
            ctx.globe.setGuessIso(guessed.iso);
            void ctx.globe.flyToIso(guessed.iso, { durationMs: 700 });
          }
        }
      }
      showReveal(false);
    }
  };

  const handleTextAnswer = (): void => {
    if (session.phase !== "prompting-text" || !session.current) return;
    const raw = input.value;
    const candidates = acceptedNamesFor(session.current);
    const result = fuzzyMatch({ input: raw, candidates, tolerance });
    session.lastFuzzyNote = result.fuzzy
      ? `Matched on a typo — the canonical spelling is "${session.current.canonicalName}".`
      : null;
    finalizeAnswer(result.matched, raw);
  };

  const onMCClick = (choice: CountryMetadata): void => {
    if (session.phase !== "prompting-mc" || !session.current) return;
    const correct = choice.iso === session.current.iso;
    // The user typed nothing (MC), so no text to feed findClosestCountry.
    // If they picked wrong, the guessed country IS the one they clicked —
    // wire it up directly so the flash works.
    if (!correct) {
      session.guessedCountry = choice;
      ctx.globe.setGuessIso(choice.iso);
      void ctx.globe.flyToIso(choice.iso, { durationMs: 700 });
    }
    finalizeAnswer(correct, "");
  };

  const onHint = (): void => {
    if (session.phase !== "prompting-text" || !session.current) return;
    session.usedHint = true;
    const name = session.current.canonicalName;
    const firstLetter = name.charAt(0);
    const blanks = name
      .slice(1)
      .replace(/[A-Za-z]/g, "_")
      .replace(/\s/g, "·");
    hintText.textContent = `Starts with: ${firstLetter}${blanks}`;
    hintText.hidden = false;
    hintBtn.disabled = true;
  };

  const onSubmit = (e: SubmitEvent): void => {
    e.preventDefault();
    if (session.phase === "prompting-text") {
      handleTextAnswer();
    } else if (session.phase === "prompting-mc") {
      // Submit doesn't apply in MC mode; ignore.
      return;
    } else {
      void advance();
    }
  };

  const onBack = (): void => {
    cancelAutoAdvance();
    if (session.missedIsos.size > 0) {
      ctx.nav.goReview({
        region,
        missedIsos: [...session.missedIsos],
      });
    } else {
      ctx.nav.goRegionPicker();
    }
  };

  form.addEventListener("submit", onSubmit);
  backBtn.addEventListener("click", onBack);
  hintBtn.addEventListener("click", onHint);

  if (import.meta.env.DEV) {
    (window as unknown as { __geoGauntlet?: unknown }).__geoGauntlet = {
      getCurrentTarget: () =>
        session.current
          ? {
              iso: session.current.iso,
              canonicalName: session.current.canonicalName,
              names: session.current.names,
            }
          : null,
      getPhase: () => session.phase,
      getMCChoices: () =>
        session.mcChoices.map((c) => ({
          iso: c.iso,
          canonicalName: c.canonicalName,
        })),
      forceTarget: (iso: string) => {
        const forced = table[iso];
        if (!forced) return false;
        cancelAutoAdvance();
        session.current = forced;
        session.lastFuzzyNote = null;
        session.guessedCountry = null;
        session.usedHint = false;
        promptEl.textContent = "What country is this?";
        feedback.hidden = true;
        feedback.textContent = "";
        feedback.className = "learn-feedback";
        hintText.hidden = true;
        submitBtn.disabled = false;
        submitBtn.textContent = "Submit";
        hintBtn.disabled = false;
        hintBtn.hidden = !hintsEnabled;
        input.disabled = false;
        input.value = "";
        session.mcChoices = [];
        setMode("prompting-text");
        ctx.globe.setTargetIso(forced.iso);
        ctx.globe.setGuessIso(null);
        void ctx.globe.flyToIso(forced.iso, { durationMs: 600 });
        return true;
      },
      forceMC: () => {
        if (!session.current) return false;
        const distractors = pickDistractors({
          target: session.current,
          pool: regionCountries,
          count: MC_CHOICE_COUNT - 1,
          rng: Math.random,
        });
        session.mcChoices = [session.current, ...distractors];
        renderMCChoices();
        promptEl.textContent = "Pick the country:";
        setMode("prompting-mc");
        return true;
      },
    };
  }

  void advance();

  return () => {
    destroyed = true;
    cancelAutoAdvance();
    form.removeEventListener("submit", onSubmit);
    backBtn.removeEventListener("click", onBack);
    hintBtn.removeEventListener("click", onHint);
    ctx.globe.setTargetIso(null);
    ctx.globe.setGuessIso(null);
    ctx.globe.setHoverEnabled(true);
    root.classList.remove("view-learn");
    if (import.meta.env.DEV) {
      delete (window as unknown as { __geoGauntlet?: unknown }).__geoGauntlet;
    }
  };
};
